import {
    useOrganizationOwnedPlanPricesQuery,
    useOrganizationPlansQuery,
} from "@api/billingOrganization.api";
import {
    clientOwnedWorkspacePricing,
    organizationOwnedWorkspacePricing,
    regularWorkspacePricing,
    PricingByPlan,
} from "@common/constants/domains/organizationBilling/helpers";
import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { SubscriptionDiscount } from "@common/types/billing";
import { useMemo } from "react";
import { useActiveOrganization } from "@hooks/useActiveOrganization";

export interface ClientPricing {
    isFetching: boolean;
    percentOffDiscount: SubscriptionDiscount | null;
    basePricing: PricingByPlan;
    organizationOwnedBillingPricing: PricingByPlan;
    organizationOwnedNextSeatPricing: PricingByPlan;
    organizationOwnedNextSeatPricingBeforeDiscount: PricingByPlan;
    clientOwnedBillingPricing: PricingByPlan;
    organizationOwnedComplimentaryPlans: FLAT_RATE_PLANS[];
    isOrganizationOwnedPricingReady: boolean;
    isOrganizationOwnedPricingError: boolean;
}

export function useClientPricing(): ClientPricing {
    const activeOrganization = useActiveOrganization();
    const { data: plansResponse, isFetching: isPlansFetching } =
        useOrganizationPlansQuery(activeOrganization?.id);
    const {
        data: organizationOwnedPlanPrices,
        isFetching: isOrganizationOwnedPricesFetching,
        isError: isOrganizationOwnedPricesError,
    } = useOrganizationOwnedPlanPricesQuery(activeOrganization?.id);
    const plans = plansResponse?.plans;

    return useMemo(() => {
        const discount = organizationOwnedPlanPrices?.discount ?? null;
        const discountPercentOff = discount?.percentOff ?? null;
        const organizationOwnedBillingPricing =
            organizationOwnedWorkspacePricing(plans ?? [], {
                percentOff: discountPercentOff,
            });

        const organizationOwnedNextSeatPricing = organizationOwnedPlanPrices
            ? regularWorkspacePricing(
                  organizationOwnedPlanPrices.plans.map((planPrice) => ({
                      plan: planPrice.plan,
                      monthlyAmountCents: planPrice.monthlyAmountCents,
                  })),
              )
            : organizationOwnedBillingPricing;

        const organizationOwnedNextSeatPricingBeforeDiscount =
            organizationOwnedPlanPrices
                ? regularWorkspacePricing(
                      organizationOwnedPlanPrices.plans.map((planPrice) => ({
                          plan: planPrice.plan,
                          monthlyAmountCents:
                              planPrice.monthlyAmountCentsBeforeDiscount,
                      })),
                  )
                : regularWorkspacePricing(plans ?? []);

        const organizationOwnedComplimentaryPlans =
            organizationOwnedPlanPrices?.plans
                .filter((planPrice) => planPrice.isComplimentary)
                .map((planPrice) => planPrice.plan) ?? [];

        return {
            isFetching: isPlansFetching || isOrganizationOwnedPricesFetching,
            percentOffDiscount: discountPercentOff ? discount : null,
            basePricing: regularWorkspacePricing(plans ?? []),
            organizationOwnedBillingPricing,
            organizationOwnedNextSeatPricing,
            organizationOwnedNextSeatPricingBeforeDiscount,
            clientOwnedBillingPricing: clientOwnedWorkspacePricing(plans ?? []),
            organizationOwnedComplimentaryPlans,
            isOrganizationOwnedPricingReady: !!organizationOwnedPlanPrices,
            isOrganizationOwnedPricingError: isOrganizationOwnedPricesError,
        };
    }, [
        plans,
        isPlansFetching,
        isOrganizationOwnedPricesFetching,
        isOrganizationOwnedPricesError,
        organizationOwnedPlanPrices,
    ]);
}
